
// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useBrandingStore = (...args) => require("%/dynamic-component-editor/stores/branding-store").default(...args);

const TYPE_OVERRIDES = {
  ButtonBranding: "ButtonElement",
};

const SKIP_KEYS = new Set(["ColorPalette", "TextHighlighting"]);

function toCamelCase(str) {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

function buildIdentifier(...parts) {
  return parts
      .filter(Boolean)
      .map((part, i) => {
        const longIdentifier = part.charAt(0).toUpperCase() + part.slice(1);
        return i === 0 ? toCamelCase(part) : longIdentifier;
      })
      .join("");
}

function isOptionsLeaf(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return false;
  }
  const keys = Object.keys(obj);
  if (keys.length === 0) {
    return true;
  }
  return keys.some(k => k.includes("."));
}

function mapElementConfig(topLevelKey, config, calculateElementOptions) {
  const results = [];

  function emit(optionsMap, variant, size) {
    const identifier = buildIdentifier(topLevelKey, variant, size);
    results.push(buildElement(topLevelKey, identifier, optionsMap ?? {}, variant, size, calculateElementOptions));
  }

  function walk(node, variant, size) {
    // Non-object or array at this level (e.g. ButtonBranding.secondary.large = Array[0])
    // Still emit the element with empty options so every variant/size is represented
    if (!node || typeof node !== "object" || Array.isArray(node)) {
      if (variant !== null) {
        emit({}, variant, size);
      }
      return;
    }

    if (isOptionsLeaf(node)) {
      emit(node, variant, size);
      return;
    }

    // Structural node — iterate children
    for (const [childKey, childValue] of Object.entries(node)) {
      if (variant === null) {
        // Children at this level are variants
        walk(childValue, childKey, null);
      } else if (size === null) {
        // Children at this level are either sizes (structural) or the options map itself
        if (!childValue || Array.isArray(childValue) || isOptionsLeaf(childValue)) {
          // childKey is a size; childValue is the options map, empty, or an array
          const identifier = buildIdentifier(topLevelKey, variant, childKey);
          results.push(buildElement(
            topLevelKey,
            identifier,
            !childValue || Array.isArray(childValue) ? {} : childValue,
            variant,
            childKey,
            calculateElementOptions,
          ));
        } else {
          // Deeper structural nesting — recurse with size
          walk(childValue, variant, childKey);
        }
      }
    }
  }

  walk(config, null, null);
  return results;
}

function buildElement(topLevelKey, identifier, optionsMap, variant, size, calculateElementOptions) {
  const elementType = TYPE_OVERRIDES[topLevelKey] ?? topLevelKey;

  return {
    timestampLastUpdated: useBrandingStore().styleVersionId,
    options: {
      ...optionsMap,
      ...calculateElementOptions(
        topLevelKey,
        variant,
        size,
      ),
      "default.text-option": variant.split("")[0].toUpperCase() + variant.slice(1),
    },
    elementTemplateId: identifier,
    type: elementType,
    identifier,
    elementType: "theme",
    brandedType: topLevelKey,
    brandedVariant: variant,
    brandedSize: size,
  };
}

export function mapBrandingConfigToElements(brandingConfiguration, calculateElementOptions = () => {}) {
  const results = [];

  for (const [topLevelKey, config] of Object.entries(brandingConfiguration)) {
    if (SKIP_KEYS.has(topLevelKey)) {
      continue;
    }
    if (!config || typeof config !== "object") {
      continue;
    }

    const mapped = mapElementConfig(topLevelKey, config, calculateElementOptions);
    results.push(...mapped);
  }

  return results;
}

export function mapElementsToBrandingConfig(elements) {
  const config = {};

  const INJECTED_KEYS = new Set(["default.text-option"]);

  for (const element of elements) {
    const { brandedType, brandedVariant, brandedSize, options } = element;

    if (!brandedType || !brandedVariant) {
      continue;
    }

    const cleanOptions = Object.fromEntries(
      Object.entries(options ?? {}).filter(([k]) => !INJECTED_KEYS.has(k)),
    );

    if (!config[brandedType]) {
      config[brandedType] = {};
    }

    if (brandedSize) {
      if (!config[brandedType][brandedVariant]) {
        config[brandedType][brandedVariant] = {};
      }
      config[brandedType][brandedVariant][brandedSize] = cleanOptions;
    } else {
      config[brandedType][brandedVariant] = cleanOptions;
    }
  }

  return config;
}

export function syncBrandingConfigurationWithDb(updateConfiguration, success = () => {}) {
  useBrandingStore().updateConfiguration({
    ...useBrandingStore().configuration,
    ...updateConfiguration,
  }, success);
}

export function themedElementIsUpToDate(version) {
  return version === useBrandingStore().styleVersionId;
}
